/**
 * 규칙 빌더 카탈로그.
 *
 * 서버 `GET /strategies/dsl/schema` 는 `{schema, available, unavailable}` 만 준다 —
 * 지표별 파라미터·라벨 메타데이터는 서버에 없다. 그래서 메타데이터는 여기(클라이언트)에
 * 두고, **서버 `available` 에 있는 지표만** 카탈로그에 올린다. 화면엔 있는데 계산은 안 되는
 * 조건을 만들지 않기 위한 교집합이다 (서버 validate 가 최종 게이트).
 */

import type {
  Cadence,
  DslIndicatorSchema,
  DslParamSchema,
  DslSchema,
  IndicatorSource,
  Interval,
  PredicateOp,
} from "@/types/api";
import {
  CADENCE,
  INDICATOR_LABEL,
  INDICATOR_SOURCE,
  PREDICATE_OP,
  RANKING_DURATION,
  RANKING_TYPE,
  SECURITY_TYPE,
  WARNING_TYPE,
} from "./labels";

/** 서버 응답 그대로. */
export interface DslSchemaResponse {
  schema: unknown;
  available: Partial<Record<IndicatorSource, string[]>>;
  unavailable: Record<string, string>;
  /**
   * 조건 트리 상한. 서버 `rules/validator.py: _check_complexity` 가 같은 값으로 저장을 막아요.
   * 예전에는 이 숫자가 여기 하드코딩(`max_entry_conditions: 4`)돼 있었고 서버에는 상한이
   * 아예 없어서, 화면이 약속한 한계와 서버가 지키는 한계가 서로 달랐습니다.
   */
  limits?: { max_entry_conditions: number; max_condition_depth: number };
}

const intParam = (
  name: string,
  label: string,
  def: number,
  min = 1,
  max = 400
): DslParamSchema => ({
  name,
  label,
  type: "int",
  default: def,
  min,
  max,
  options: null,
});

const numberParam = (name: string, label: string, def: number): DslParamSchema => ({
  name,
  label,
  type: "number",
  default: def,
  min: 0,
  max: 100,
  options: null,
});

const enumParam = (
  name: string,
  label: string,
  def: string,
  options: string[]
): DslParamSchema => ({
  name,
  label,
  type: "enum",
  default: def,
  min: null,
  max: null,
  options,
});

/** 서버 `_DEFAULT_PERIODS`(registry.py)와 같은 기본값. 없는 이름은 20. */
const DEFAULT_PERIODS: Record<string, number> = {
  rsi: 14,
  atr: 14,
  natr: 14,
  stoch: 14,
  adx: 14,
  plus_di: 14,
  minus_di: 14,
  mfi: 14,
  cci: 20,
  bbands: 20,
  stdev: 20,
  hist_vol: 20,
  donchian_high: 20,
  donchian_low: 20,
  donchian_pos: 20,
  keltner_upper: 20,
  keltner_lower: 20,
  volume_ratio: 20,
  volume_zscore: 20,
  slope: 20,
  linreg_r2: 20,
  roc: 10,
  momentum: 10,
  return_pct: 1,
  high_n: 20,
  low_n: 20,
  pos_in_range_n: 20,
  opening_range_high: 5,
  opening_range_low: 5,
};

/** period 파라미터를 받는 지표 (레지스트리 계산 규칙과 동일 집합). */
const PERIOD_NAMES = new Set([
  ...Object.keys(DEFAULT_PERIODS),
  "sma",
  "ema",
  "wma",
  "rs_vs_index",
  "beta_n",
  "corr_n",
]);

const PATTERN_NAMES = [
  "engulfing_bull",
  "engulfing_bear",
  "hammer",
  "shooting_star",
  "doji",
  "inside",
  "outside",
];

/** 지표별 추가 파라미터·출력. 목록에 없으면 period(해당 시)만 노출한다. */
const EXTRA_PARAMS: Record<string, DslParamSchema[]> = {
  macd: [
    intParam("fast", "단기", 12),
    intParam("slow", "장기", 26),
    intParam("signal", "시그널", 9),
  ],
  bbands: [numberParam("k", "표준편차 배수", 2)],
  stoch: [intParam("d_period", "%D 기간", 3)],
  keltner_upper: [numberParam("mult", "ATR 배수", 2)],
  keltner_lower: [numberParam("mult", "ATR 배수", 2)],
  pattern: [enumParam("name", "패턴", "doji", PATTERN_NAMES)],
};

const OUTPUTS: Record<string, string[]> = {
  macd: ["macd", "signal", "hist"],
  bbands: ["mid", "upper", "lower"],
  stoch: ["k", "d"],
};

function indicatorSchema(source: IndicatorSource, name: string): DslIndicatorSchema {
  const params: DslParamSchema[] = [];
  if (PERIOD_NAMES.has(name)) params.push(intParam("period", "기간", DEFAULT_PERIODS[name] ?? 20));
  params.push(...(EXTRA_PARAMS[name] ?? []));
  return {
    name,
    label:
      INDICATOR_LABEL[`${source}.${name}`]?.replace("{n}", "N") ??
      INDICATOR_LABEL[`candle.${name}`]?.replace("{n}", "N") ??
      name,
    params,
    outputs: OUTPUTS[name] ?? null,
    note: name === "vwap_session" ? "실제 체결 VWAP이 아닌 근사값이에요." : null,
    unit: null,
  };
}

const CANDLE_INTERVALS: Interval[] = ["1d", "1m"];

const TERNARY_OPS = new Set<PredicateOp>(["between", "not_between"]);
const UNARY_OPS = new Set<PredicateOp>(["rising", "falling"]);

/** 서버 응답을 규칙 빌더가 쓰는 카탈로그로 변환한다. */
export function buildDslCatalog(response: DslSchemaResponse): DslSchema {
  const sources = (Object.entries(response.available) as Array<[IndicatorSource, string[]]>).map(
    ([source, names]) => ({
      source,
      label: INDICATOR_SOURCE[source] ?? source,
      intervals:
        source === "candle" || source === "index" || source === "bond"
          ? CANDLE_INTERVALS
          : (["1d"] as Interval[]),
      indicators: [...names].sort().map((name) => indicatorSchema(source, name)),
      note:
        source === "investor"
          ? "KOSPI·KOSDAQ 시장 전체 수급이에요. 개별 종목 수급은 제공되지 않아요."
          : null,
    })
  );
  return {
    sources,
    operators: (Object.entries(PREDICATE_OP) as Array<[PredicateOp, string]>).map(
      ([op, label]) => ({
        op,
        label,
        arity: TERNARY_OPS.has(op) ? 3 : UNARY_OPS.has(op) ? 1 : 2,
      })
    ),
    ranking: {
      types: Object.entries(RANKING_TYPE).map(([value, label]) => ({
        value: value as keyof typeof RANKING_TYPE,
        label,
      })),
      durations: Object.entries(RANKING_DURATION).map(([value, label]) => ({
        value: value as keyof typeof RANKING_DURATION,
        label,
      })),
      // 미국 시장은 이제 열려 있다 — 서버가 랭킹·캘린더·1분봉 스윕·통화별 장부를 전부
      // US 로도 돌린다(`tasks/market.py: SWEEP_MARKETS`). 여기서 KR 만 노출하면 편집기의
      // 시장 선택도 KR 하나로 굳어서, 서버가 지원하는 기능이 화면에서 도달 불가가 된다.
      market_countries: [
        { value: "KR", label: "국내" },
        { value: "US", label: "미국" },
      ],
    },
    cadences: Object.entries(CADENCE).map(([value, label]) => ({
      value: value as Cadence,
      label,
    })),
    security_types: Object.entries(SECURITY_TYPE)
      .filter(([value]) => value !== "unknown")
      .map(([value, label]) => ({ value: value as keyof typeof SECURITY_TYPE, label })),
    warning_types: Object.entries(WARNING_TYPE)
      .filter(([value]) => value !== "unknown")
      .map(([value, label]) => ({ value: value as keyof typeof WARNING_TYPE, label })),
    // 폴백은 서버가 `limits` 를 아직 안 주는 배포와 섞였을 때만 쓰여요 — 서버 상한보다
    // 낮게 잡아서 화면이 저장 불가한 규칙을 만들지 않게 합니다.
    max_entry_conditions: response.limits?.max_entry_conditions ?? 4,
    max_condition_depth: response.limits?.max_condition_depth ?? 2,
  };
}
